import React, { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../App';
import VoiceProfileSetup from '../components/VoiceProfileSetup';
import SignatureEditor from '../components/SignatureEditor';
import PerformanceDataEditor from '../components/PerformanceDataEditor';

const Settings = () => {
  const { user, setUser } = useAuth();
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  
  // Profile editing state
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', preamble: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Signature editing state
  const [editingSignature, setEditingSignature] = useState(false);
  const [signatureHtml, setSignatureHtml] = useState('');
  const [savingSignature, setSavingSignature] = useState(false);
  
  // Voice profile state
  const [voiceProfile, setVoiceProfile] = useState(null);
  const [showVoiceSetup, setShowVoiceSetup] = useState(false);
  const [loadingVoice, setLoadingVoice] = useState(true);

  // Org state
  const [org, setOrg] = useState(null);
  const [orgMembers, setOrgMembers] = useState([]);
  const [orgRole, setOrgRole] = useState(null);
  const [orgInvites, setOrgInvites] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [inviteLink, setInviteLink] = useState('');

  // Change password state
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', newPw: '', confirm: '' });
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');
    if (passwordForm.newPw.length < 8) { setPasswordError('New password must be at least 8 characters'); return; }
    if (passwordForm.newPw !== passwordForm.confirm) { setPasswordError('Passwords do not match'); return; }
    setSavingPassword(true);
    try {
      await api.put('/auth/password', { current_password: passwordForm.current, new_password: passwordForm.newPw });
      setPasswordSuccess('Password updated successfully');
      setPasswordForm({ current: '', newPw: '', confirm: '' });
      setTimeout(() => { setChangingPassword(false); setPasswordSuccess(''); }, 2000);
    } catch (e) {
      setPasswordError(e.response?.data?.error || 'Failed to change password');
    } finally { setSavingPassword(false); }
  };

  const fetchOrg = async () => {
    try {
      const [orgRes, invitesRes] = await Promise.all([
        api.get('/org'),
        api.get('/org/invites').catch(() => ({ data: { invites: [] } })),
      ]);
      setOrg(orgRes.data.org);
      setOrgMembers(orgRes.data.members || []);
      setOrgRole(orgRes.data.role);
      setOrgInvites(invitesRes.data.invites || []);
    } catch (e) {
      console.error('Org fetch error:', e);
    }
  };

  const sendInvite = async () => {
    if (!inviteEmail) return;
    setInviting(true);
    try {
      const res = await api.post('/org/invite', { email: inviteEmail, role: inviteRole });
      const token = res.data.invite?.token;
      if (token) {
        const link = `${window.location.origin}/invite/${token}`;
        setInviteLink(link);
      }
      setInviteEmail('');
      fetchOrg();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to send invite');
    } finally { setInviting(false); }
  };

  const cancelInvite = async (id) => {
    try {
      await api.delete(`/org/invite/${id}`);
      fetchOrg();
    } catch (e) { alert('Failed to cancel invite'); }
  };

  const changeRole = async (userId, newRole) => {
    try {
      await api.put(`/org/member/${userId}/role`, { role: newRole });
      fetchOrg();
    } catch (e) { alert(e.response?.data?.error || 'Failed to change role'); }
  };

  const removeMember = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from the organization?`)) return;
    try {
      await api.delete(`/org/member/${userId}`);
      fetchOrg();
    } catch (e) { alert(e.response?.data?.error || 'Failed to remove member'); }
  };

  useEffect(() => {
    if (user) {
      setProfileForm({ 
        name: user.name || '', 
        preamble: user.preamble || ''
      });
      setSignatureHtml(user.email_signature || '');
      fetchVoiceProfile();
      fetchOrg();
    }
  }, [user]);

  const fetchVoiceProfile = async () => {
    setLoadingVoice(true);
    try {
      const res = await api.get('/ai/voice-profile');
      setVoiceProfile(res.data.profile);
    } catch (error) {
      console.error('Failed to fetch voice profile:', error);
    } finally {
      setLoadingVoice(false);
    }
  };

  const connectGmail = async () => {
    setConnecting(true);
    try {
      const res = await api.get('/gmail/auth-url');
      
      // Open popup for OAuth
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      const popup = window.open(
        res.data.authUrl,
        'Gmail Authorization',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Listen for the callback
      const handleMessage = async (event) => {
        if (event.data.type === 'GMAIL_AUTH_SUCCESS') {
          const { code } = event.data;
          try {
            await api.post('/gmail/callback', { code });
            setUser({ ...user, gmail_connected: true });
            popup.close();
          } catch (error) {
            alert('Failed to connect Gmail');
          }
        }
        window.removeEventListener('message', handleMessage);
        setConnecting(false);
      };

      window.addEventListener('message', handleMessage);

      // Check if popup was closed without completing
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          setConnecting(false);
          window.removeEventListener('message', handleMessage);
        }
      }, 1000);
    } catch (error) {
      console.error('Failed to get auth URL:', error);
      setConnecting(false);
    }
  };

  const disconnectGmail = async () => {
    if (!window.confirm('Disconnect Gmail? Active campaigns will be paused.')) return;
    
    setDisconnecting(true);
    try {
      await api.post('/gmail/disconnect');
      setUser({ ...user, gmail_connected: false });
    } catch (error) {
      alert('Failed to disconnect Gmail');
    } finally {
      setDisconnecting(false);
    }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const res = await api.put('/auth/profile', profileForm);
      setUser({ ...user, ...res.data.user });
      setEditingProfile(false);
    } catch (error) {
      alert('Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const saveSignature = async () => {
    setSavingSignature(true);
    try {
      const res = await api.put('/auth/profile', { email_signature: signatureHtml });
      setUser({ ...user, ...res.data.user });
      setEditingSignature(false);
    } catch (error) {
      alert('Failed to save signature');
    } finally {
      setSavingSignature(false);
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Profile</h1>
        <p className="text-zinc-400 mt-1">Manage your account, integrations, and preferences</p>
      </div>

      {/* Gmail Integration */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-6">
        <h2 className="text-lg font-semibold text-white mb-4">Gmail Integration</h2>
        <p className="text-zinc-400 text-sm mb-6">
          Connect your Gmail account to send emails directly from the platform. 
          We use OAuth, so we never see your password.
        </p>

        {user?.gmail_connected ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                <span className="text-green-400">✓</span>
              </div>
              <div>
                <div className="text-white font-medium">Gmail Connected</div>
                <div className="text-zinc-500 text-sm">Ready to send emails</div>
              </div>
            </div>
            <button
              onClick={disconnectGmail}
              disabled={disconnecting}
              className="bg-red-500/20 text-red-400 px-4 py-2 rounded-lg hover:bg-red-500/30 transition-colors disabled:opacity-50"
            >
              {disconnecting ? 'Disconnecting...' : 'Disconnect'}
            </button>
          </div>
        ) : (
          <button
            onClick={connectGmail}
            disabled={connecting}
            className="bg-white text-black px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-3"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {connecting ? 'Connecting...' : 'Connect Gmail'}
          </button>
        )}
      </div>

      {/* Change Password */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-6">
        <h2 className="text-lg font-semibold text-white mb-4">Change Password</h2>
        {!changingPassword ? (
          <button
            onClick={() => setChangingPassword(true)}
            className="text-sm text-zinc-400 hover:text-white border border-zinc-700 px-4 py-2 rounded-lg hover:border-zinc-500 transition-colors"
          >
            Update password
          </button>
        ) : (
          <div className="space-y-3 max-w-sm">
            <input
              type="password"
              placeholder="Current password"
              value={passwordForm.current}
              onChange={e => setPasswordForm(f => ({ ...f, current: e.target.value }))}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2.5 text-white text-sm placeholder-zinc-500 focus:outline-none focus:border-[#1EE4BC]"
            />
            <input
              type="password"
              placeholder="New password (min 8 characters)"
              value={passwordForm.newPw}
              onChange={e => setPasswordForm(f => ({ ...f, newPw: e.target.value }))}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2.5 text-white text-sm placeholder-zinc-500 focus:outline-none focus:border-[#1EE4BC]"
            />
            <input
              type="password"
              placeholder="Confirm new password"
              value={passwordForm.confirm}
              onChange={e => setPasswordForm(f => ({ ...f, confirm: e.target.value }))}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2.5 text-white text-sm placeholder-zinc-500 focus:outline-none focus:border-[#1EE4BC]"
            />
            {passwordError && <p className="text-red-400 text-xs">{passwordError}</p>}
            {passwordSuccess && <p className="text-green-400 text-xs">{passwordSuccess}</p>}
            <div className="flex gap-2">
              <button
                onClick={handleChangePassword}
                disabled={savingPassword}
                className="bg-[#1EE4BC] text-black font-semibold px-4 py-2 rounded-lg text-sm hover:opacity-90 disabled:opacity-50"
              >
                {savingPassword ? 'Saving...' : 'Update Password'}
              </button>
              <button
                onClick={() => { setChangingPassword(false); setPasswordForm({ current: '', newPw: '', confirm: '' }); setPasswordError(''); setPasswordSuccess(''); }}
                className="text-zinc-400 hover:text-white px-4 py-2 rounded-lg text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* AI Voice Profile */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-6">
        <h2 className="text-lg font-semibold text-white mb-4">AI Writing Voice</h2>
        <p className="text-zinc-400 text-sm mb-6">
          Set up your writing voice so AI-generated emails sound like you, not a robot.
        </p>

        {loadingVoice ? (
          <div className="text-zinc-500">Loading...</div>
        ) : showVoiceSetup ? (
          <div>
            <VoiceProfileSetup 
              isModal={false}
              onComplete={() => {
                setShowVoiceSetup(false);
                fetchVoiceProfile();
              }} 
            />
            <button
              onClick={() => setShowVoiceSetup(false)}
              className="mt-4 text-zinc-400 hover:text-white text-sm"
            >
              Cancel
            </button>
          </div>
        ) : voiceProfile?.setup_completed ? (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                  <span className="text-green-400">✓</span>
                </div>
                <div>
                  <div className="text-white font-medium">Voice Profile Active</div>
                  <div className="text-zinc-500 text-sm capitalize">
                    {voiceProfile.writing_style} style, formality {voiceProfile.formality_level}/5
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowVoiceSetup(true)}
                className="text-nextide-turquoise hover:opacity-80 text-sm font-medium"
              >
                Edit
              </button>
            </div>
            
            {voiceProfile.voice_summary && (
              <div className="bg-zinc-800 rounded-lg p-4 mt-4">
                <div className="text-xs text-zinc-500 mb-2">AI's understanding of your voice:</div>
                <div className="text-sm text-zinc-300">{voiceProfile.voice_summary}</div>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => setShowVoiceSetup(true)}
            className="bg-nextide-turquoise text-black px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity"
          >
            Set Up Voice Profile
          </button>
        )}
      </div>

      {/* Account Info */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Profile</h2>
          {!editingProfile && (
            <button
              onClick={() => setEditingProfile(true)}
              className="text-nextide-turquoise hover:opacity-80 text-sm font-medium"
            >
              Edit
            </button>
          )}
        </div>
        
        {editingProfile ? (
          <div className="space-y-4">
            <div>
              <label className="block text-zinc-400 text-sm mb-1">Name</label>
              <input
                type="text"
                value={profileForm.name}
                onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-nextide-turquoise"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="block text-zinc-400 text-sm mb-1">Email</label>
              <div className="text-zinc-500 text-sm">{user?.email}</div>
              <div className="text-zinc-600 text-xs mt-1">Email cannot be changed</div>
            </div>
            <div>
              <label className="block text-zinc-400 text-sm mb-1">Personal Preamble</label>
              <textarea
                value={profileForm.preamble}
                onChange={(e) => setProfileForm({ ...profileForm, preamble: e.target.value })}
                rows={4}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-nextide-turquoise text-sm"
                placeholder="Quick context on me: I'm [Name], [title/role]. I've [background/experience]. I [reason for reaching out / value proposition]."
              />
              <div className="text-zinc-500 text-xs mt-1">
                This appears at the start of your cold outreach emails. Keep it personal and credible.
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => {
                  setEditingProfile(false);
                  setProfileForm({ 
                    name: user?.name || '', 
                    preamble: user?.preamble || ''
                  });
                }}
                className="px-4 py-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700"
              >
                Cancel
              </button>
              <button
                onClick={saveProfile}
                disabled={savingProfile}
                className="px-4 py-2 bg-nextide-turquoise text-black rounded-lg font-medium hover:opacity-90 disabled:opacity-50"
              >
                {savingProfile ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="text-zinc-400 text-sm mb-1">Name</div>
              <div className="text-white">{user?.name}</div>
            </div>
            <div>
              <div className="text-zinc-400 text-sm mb-1">Email</div>
              <div className="text-white">{user?.email}</div>
            </div>
            {user?.preamble && (
              <div>
                <div className="text-zinc-400 text-sm mb-1">Personal Preamble</div>
                <div className="text-zinc-300 text-sm bg-zinc-800 rounded-lg p-3">{user.preamble}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Email Signature */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Email Signature</h2>
            <p className="text-zinc-500 text-sm mt-1">
              Added to the end of all outgoing emails. Supports images, links, and formatting.
            </p>
          </div>
          {!editingSignature && (
            <button
              onClick={() => setEditingSignature(true)}
              className="text-nextide-turquoise hover:opacity-80 text-sm font-medium"
            >
              {user?.email_signature ? 'Edit' : 'Add Signature'}
            </button>
          )}
        </div>
        
        {editingSignature ? (
          <div className="space-y-4">
            <SignatureEditor
              value={signatureHtml}
              onChange={setSignatureHtml}
            />
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => {
                  setEditingSignature(false);
                  setSignatureHtml(user?.email_signature || '');
                }}
                className="px-4 py-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700"
              >
                Cancel
              </button>
              <button
                onClick={saveSignature}
                disabled={savingSignature}
                className="px-4 py-2 bg-nextide-turquoise text-black rounded-lg font-medium hover:opacity-90 disabled:opacity-50"
              >
                {savingSignature ? 'Saving...' : 'Save Signature'}
              </button>
            </div>
          </div>
        ) : user?.email_signature ? (
          <div className="bg-white rounded-lg p-4">
            <div 
              className="text-black text-sm"
              dangerouslySetInnerHTML={{ __html: user.email_signature }}
            />
          </div>
        ) : (
          <div className="bg-zinc-800 rounded-lg p-6 text-center">
            <div className="text-zinc-500 mb-3">No signature set up yet</div>
            <button
              onClick={() => setEditingSignature(true)}
              className="text-nextide-turquoise hover:underline text-sm"
            >
              Add your email signature
            </button>
          </div>
        )}
      </div>

      {/* Performance Data */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-2">Performance Data</h2>
        <p className="text-zinc-500 text-sm mb-4">
          Add your real stats and case studies. The AI will only reference these verified numbers instead of hallucinating.
        </p>
        <PerformanceDataEditor />
      </div>

      {/* Developer — API Token */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-2">Developer</h2>
        <p className="text-zinc-500 text-sm mb-4">
          Generate an API token to connect the Chrome extension or external integrations.
        </p>
        <ApiTokenSection />
      </div>
    </div>
  );
};


// ============================================================
// API TOKEN SECTION
// ============================================================
const ApiTokenSection = () => {
  const [token, setToken] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateToken = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/auth/api-token');
      setToken(res.data.token);
    } catch (e) {
      alert('Failed to generate token');
    } finally {
      setGenerating(false);
    }
  };

  const copyToken = () => {
    if (!token) return;
    navigator.clipboard.writeText(token).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div>
      {!token ? (
        <button
          onClick={generateToken}
          disabled={generating}
          className="px-4 py-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 text-sm font-medium disabled:opacity-50"
        >
          {generating ? 'Generating...' : 'Generate API Token'}
        </button>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={token}
              className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-xs text-zinc-300 font-mono"
            />
            <button
              onClick={copyToken}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                copied
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                  : 'bg-nextide-turquoise text-black hover:brightness-110'
              }`}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <p className="text-xs text-amber-400/80">
            This token expires in 1 year. Treat it like a password — don't share it. You can generate a new one anytime.
          </p>
        </div>
      )}
    </div>
  );
};


export default Settings;
